import React, { useState } from 'react';
import { Grid, Typography, Stack, AppBar } from '@mui/material';
import { useParams, useSearchParams } from 'react-router-dom';
import { useGetDraftQuery, useUpdateDraftMutation, useGetProcedureCampaignDraftsQuery, useLogUserActionMutation } from '../../../api/physicianDashboardSlice';
import PeerComparisonTitle from './PeerComparisonTitle';
import ReviewTabs from '../ReviewPageComponents/ReviewTabs';
import LoadingIndicator from '../../LoadingIndicator';
import CategoryView from '../ReviewPageComponents/CategoryView';
import { useLocation } from 'react-router-dom';
import ItemDetails from '../ItemDetails/ItemDetails';
import { useScrollRestore } from '../../Hooks/useScrollRestore';
import CompleteModal from '../CompleteModal';
import SubmitReviewButton from '../SubmitReviewButton';

function PeerComparison() {
    const { save: saveScroll, restore: restoreScroll } = useScrollRestore();
    const [searchParams] = useSearchParams();
    const category = searchParams.get('category');
    const { draftId } = useParams();
    const initialSelectedPeerId = useLocation().state?.selectedPeer || null;
    const [selectedPeer, setSelectedPeer] = useState(initialSelectedPeerId);
    const [isShowingItemDetails, setIsShowingItemDetails] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedTab, setSelectedTab] = useState(selectedPeer ? 'comparison' : 'my_card');
    const [isShowingCompleteModal, setIsShowingCompleteModal] = useState(false);
    const [logUserAction] = useLogUserActionMutation();
    
    const {
        data: draft = {},
        isLoading: draftLoading,
        isSuccess: draftLoaded,
        error: draftError
    } = useGetDraftQuery(draftId);

    const queryParams = draftLoaded
        ? { campaignId: draft.campaign_id, procedureId: draft.card.primary_procedure_id }
        : undefined;
    
    const {
        data: procedureDrafts,
        isLoading: procedureDraftsLoading,
        isSuccess: procedureDraftsLoaded,
        error: procedureDraftsError
    } = useGetProcedureCampaignDraftsQuery(
        queryParams,
        { enabled: draftLoaded } // only fetch if draft is loaded
    );

    const [
        submitChanges,
        { isLoading: isSubmittingChanges },
    ] = useUpdateDraftMutation();

    const handleSubmitChanges = async () => {
        await submitChanges({
            draftId: draft.id,
            status: 'submitted'
        });
        logUserAction({ action: 'submitted_draft', data: { draftId: draft.id, location: 'peer_comparison' } });
        setIsShowingCompleteModal(true);
    }

    const seeItemDetails = (item) => {
        saveScroll();
        setSelectedItem(item);
        setIsShowingItemDetails(true);
    }

    const closeItemDetails = () => {
        setIsShowingItemDetails(false);
        restoreScroll();
    }
    
    const peerDrafts = procedureDrafts?.filter(d => d.id !== draft.id) || [];
    const peerList = peerDrafts.map(d => ({ draftId: d.id, first_name: d.card.doctor.first_name, last_name: d.card.doctor.last_name, physicianId: d.card.doctor.id }));
    const peerDraft = peerDrafts.find(d => d.card.doctor.id === selectedPeer) || null;

    if (draftLoading) return <LoadingIndicator />;
    if (draftError) return <Typography variant='normal'>There was an error loading this data. Please try again later.</Typography>;

    return (
        <Grid container sx={{ backgroundColor: '#EDEDED' }}>
            <Grid item md={3} />
            <Grid item xs={12} md={6}>
                {/* Title/Header and tabs */}
                <PeerComparisonTitle draft={draft} peerList={peerList} selectedPeer={selectedPeer} setSelectedPeer={setSelectedPeer} isLoading={draftLoading || procedureDraftsLoading} />
                <ReviewTabs selectedTab={selectedTab} setSelectedTab={setSelectedTab} variant="peer_comparison" logUserAction={logUserAction} />

                { (selectedTab === 'comparison' || selectedTab === 'peer_card') && procedureDraftsLoading && <LoadingIndicator /> }

                {/* Elements based on tabs */}
                { selectedTab === 'my_card' && 
                <CategoryView physicianDraft={draft} isLoading={draftLoading} variant="physician_draft" seeItemDetails={seeItemDetails} defaultCategory={category} /> }
                { selectedTab === 'comparison' && procedureDraftsLoaded &&
                <CategoryView physicianDraft={draft} peerDraft={peerDraft} isLoading={draftLoading || procedureDraftsLoading} variant="comparison" seeItemDetails={seeItemDetails} defaultCategory={category} /> }
                { selectedTab === 'peer_card' && procedureDraftsLoaded &&
                <CategoryView peerDraft={peerDraft} isLoading={draftLoading || procedureDraftsLoading} variant="peer_draft" seeItemDetails={seeItemDetails} defaultCategory={category} /> }

                {/* Error Handling */}
                { procedureDraftsError && selectedTab !== 'my_card' &&
                <Stack p={2} alignItems='center' justifyContent='center' sx={{ height: '60vh' }}>
                    <Typography variant='normal'>There was an error loading this data. Please try again later.</Typography>
                </Stack> }

                <AppBar position='sticky' elevation={10} sx={{ marginTop: 5, paddingLeft: 3, paddingRight: 3, bottom: 16, backgroundColor: '#FFFFFF0F', alignSelf: 'center', boxShadow: 0 }}>
                    <SubmitReviewButton onClick={handleSubmitChanges} loading={isSubmittingChanges} />
                </AppBar>

                {/* Item Details */}
                <ItemDetails open={isShowingItemDetails} close={closeItemDetails} draft={draft} item={selectedItem} peerDraft={peerDraft} variant='peer_comparison' />

                { draftLoaded &&
                    <CompleteModal 
                        open={isShowingCompleteModal}
                        setOpen={setIsShowingCompleteModal}
                        physician={draft.card.doctor}
                        campaignId={draft.campaign_id}
                        variant='standard'
                    />
                }
            </Grid>
            <Grid item md={3} />
        </Grid>
    );
}

export default PeerComparison;